import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, ArrowLeft } from 'lucide-react'
import { dashboardService, type RiskIndicator } from '../services/dashboard'
import { toast } from 'sonner'
import { getErrorMessage } from '../utils/apiTransformers'

export default function RisksPage() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [risks, setRisks] = useState<RiskIndicator[]>([])
  const [stats, setStats] = useState({
    total_risks: 0,
    high_severity: 0,
    medium_severity: 0,
  })

  useEffect(() => {
    fetchRisks()
  }, [])

  const fetchRisks = async () => {
    setLoading(true)
    try {
      const data = await dashboardService.getRiskIndicators()
      setRisks(data.risks)
      setStats({
        total_risks: data.total_risks,
        high_severity: data.high_severity,
        medium_severity: data.medium_severity,
      })
    } catch (error) {
      console.error('Error fetching risks:', error)
      toast.error(getErrorMessage(error) || 'Failed to load portfolio risks')
    } finally {
      setLoading(false)
    }
  }

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high':
        return 'bg-red-100 text-red-700 border-red-300'
      case 'medium':
        return 'bg-yellow-100 text-yellow-700 border-yellow-300'
      default:
        return 'bg-blue-100 text-blue-700 border-blue-300'
    }
  }

  const getSeverityLabel = (severity: string) => {
    switch (severity) {
      case 'high':
        return 'Critical'
      case 'medium':
        return 'High'
      default:
        return 'Medium'
    }
  }

  const handleRiskClick = (risk: RiskIndicator) => {
    if (risk.project_id) {
      navigate(`/project/${risk.project_id}`)
    }
  }

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto bg-[#f0f4f8] min-h-screen">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-[#102a43] via-[#486581] to-[#102a43] rounded-xl p-6 text-white shadow-lg border border-[#829ab1]/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 hover:bg-[#486581] rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-3xl mb-2">Portfolio Risks</h1>
              <p className="text-[#bcccdc]">Comprehensive risk analysis across all portfolios</p>
            </div>
          </div>
        </div>
      </div>

      {/* Risk Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-6 rounded-xl border-2 border-[#bcccdc] shadow-lg">
          <div className="text-sm text-[#486581] mb-2">Total Risks</div>
          <div className="text-3xl font-bold text-[#102a43]">
            {loading ? '...' : stats.total_risks}
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border-2 border-[#bcccdc] shadow-lg">
          <div className="text-sm text-[#486581] mb-2">Critical Risks</div>
          <div className="text-3xl font-bold text-red-600">
            {loading ? '...' : stats.high_severity}
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border-2 border-[#bcccdc] shadow-lg">
          <div className="text-sm text-[#486581] mb-2">High Priority</div>
          <div className="text-3xl font-bold text-yellow-600">
            {loading ? '...' : stats.medium_severity}
          </div>
        </div>
      </div>

      {/* Risks List */}
      <div className="bg-white p-6 rounded-xl border-2 border-[#bcccdc] shadow-lg">
        <h2 className="text-xl font-semibold text-[#102a43] mb-4">All Portfolio Risks</h2>
        
        {loading ? (
          <div className="text-center py-12 text-[#486581]">Loading risks...</div>
        ) : risks.length === 0 ? (
          <div className="text-center py-12 text-[#486581]">
            <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-[#829ab1]" />
            <p>No risks identified at this time.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {risks.map((risk, index) => (
              <div
                key={index}
                onClick={() => handleRiskClick(risk)}
                className={`flex items-center gap-4 p-4 bg-[#f0f4f8] border-2 border-[#bcccdc] rounded-lg hover:bg-white hover:border-[#829ab1] hover:shadow-md transition-all ${
                  risk.project_id ? 'cursor-pointer' : ''
                }`}
              >
                <div
                  className={`p-2 rounded-lg ${
                    risk.severity === 'high' ? 'bg-red-100' : 'bg-yellow-100'
                  }`}
                >
                  <AlertTriangle
                    className={`w-5 h-5 flex-shrink-0 ${
                      risk.severity === 'high' ? 'text-red-600' : 'text-yellow-600'
                    }`}
                  />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="font-medium text-[#102a43]">
                      {risk.message.split(':')[0] || risk.type.replace('_', ' ')}
                    </h3>
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-medium border ${getSeverityColor(risk.severity)}`}
                    >
                      {getSeverityLabel(risk.severity)}
                    </span>
                  </div>
                  <p className="text-sm text-[#486581]">
                    {risk.project_name || 'Portfolio-wide'}
                  </p>
                  <p className="text-xs text-[#829ab1] mt-1">{risk.message}</p>
                </div>
                <div className="text-right">
                  {risk.budget && risk.actual_cost && (
                    <div className="text-sm text-red-600 font-medium mb-1">
                      ${((risk.actual_cost - risk.budget) / 1000).toFixed(1)}K variance
                    </div>
                  )}
                  {risk.progress !== undefined && (
                    <div className="text-sm text-[#829ab1]">
                      Progress: <span className="text-[#486581] font-medium">{risk.progress}%</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

